import { z } from 'zod';

// Grouped Data Schema
export const DashboardGroupedDataResponseSchema = z.object({
  purchases: z.array(
    z.object({
      _id: z.object({
        day: z.number(),
        month: z.number(),
        year: z.number(),
      }),
      totalPurchases: z.number(),
      count: z.number(),
    }),
  ),
  usersByProduct: z.array(
    z.object({
      productName: z.string(),
      userCount: z.number(),
    }),
  ),
  usersByPlatform: z.array(
    z.object({
      _id: z.string(), // Platform name
      userCount: z.number(),
    }),
  ),
  usersByHostApp: z.array(
    z.object({
      _id: z.string(), // Host App name
      userCount: z.number(),
    }),
  ),
});

export type DashboardGroupedDataResponse = z.infer<
  typeof DashboardGroupedDataResponseSchema
>;

// Recent Users Schema
export const RecentUsersResponseSchema = z.object({
  docs: z.array(
    z.object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      lastLogin: z.string(),
    }),
  ),
  totalDocs: z.number(),
  limit: z.number(),
  page: z.number(),
  totalPages: z.number(),
  hasNextPage: z.boolean(),
  hasPrevPage: z.boolean(),
});

export type RecentUsersResponse = z.infer<typeof RecentUsersResponseSchema>;

export const RecentUsersRequestSchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
});

export type RecentUsersRequest = z.infer<typeof RecentUsersRequestSchema>;

// Refunds Schema
export const RefundsResponseSchema = z.object({
  docs: z.array(
    z.object({
      transactionId: z.string(),
      amount: z.number(),
      status: z.string().optional(),
      note: z.string().optional(),
    }),
  ),
  totalDocs: z.number(),
  limit: z.number(),
  page: z.number(),
  totalPages: z.number(),
  hasNextPage: z.boolean(),
  hasPrevPage: z.boolean(),
});

export type RefundsResponse = z.infer<typeof RefundsResponseSchema>;

export const RefundsRequestSchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
});

export type RefundsRequest = z.infer<typeof RefundsRequestSchema>;

export const SalesInsightsRequestSchema = z.object({
  from: z.string(),
  to: z.string(),
  productIds: z.array(z.string()).optional(),
  churnDenominatorFrom: z.string().optional(),
  churnDenominatorTo: z.string().optional(),
  churnRenewalsFrom: z.string().optional(),
  churnRenewalsTo: z.string().optional(),
});

export type SalesInsightsRequest = z.infer<typeof SalesInsightsRequestSchema>;

export const SalesInsightsResponseSchema = z.object({
  granularity: z.enum(['month', 'week']),
  totals: z.object({
    count: z.number(),
    revenue: z.number(),
  }),
  salesOverTime: z.array(
    z.object({
      period: z.string(),
      label: z.string(),
      count: z.number(),
      revenue: z.number(),
      initial: z.number(),
      renewal: z.number(),
      plan_change: z.number(),
      other: z.number(),
    }),
  ),
  subscriptionTotals: z.array(
    z.object({
      category: z.string(),
      label: z.string(),
      count: z.number(),
      revenue: z.number(),
    }),
  ),
  revenueByProduct: z.array(
    z.object({
      productName: z.string(),
      count: z.number(),
      revenue: z.number(),
    }),
  ),
  churnSnapshot: z
    .object({
      denominatorLabel: z.string(),
      denominatorCount: z.number(),
      denominatorTotal: z.number(),
      renewalsLabel: z.string(),
      renewalsCount: z.number(),
      renewalsTotal: z.number(),
      renewalRatePercent: z.number().nullable(),
    })
    .nullable(),
  untaggedRenewalHint: z.number(),
});

export type SalesInsightsResponse = z.infer<typeof SalesInsightsResponseSchema>;

export const SalesSegmentTransactionsRequestSchema = z.object({
  from: z.string(),
  to: z.string(),
  productIds: z.array(z.string()).optional(),
  period: z.string(),
  granularity: z.enum(['month', 'week']),
  category: z.enum(['initial', 'renewal', 'plan_change', 'other']),
});

export type SalesSegmentTransactionsRequest = z.infer<
  typeof SalesSegmentTransactionsRequestSchema
>;

const salesTransactionRowSchema = z.object({
  id: z.string(),
  date: z.string(),
  productName: z.string(),
  email: z.string(),
  amount: z.number(),
  saleType: z.string(),
  billingReason: z.string().nullable(),
});

export const UntaggedRenewalTransactionsRequestSchema = z.object({
  from: z.string(),
  to: z.string(),
  productIds: z.array(z.string()).optional(),
});

export type UntaggedRenewalTransactionsRequest = z.infer<
  typeof UntaggedRenewalTransactionsRequestSchema
>;

export const UntaggedRenewalTransactionsResponseSchema = z.object({
  transactions: z.array(salesTransactionRowSchema),
  total: z.number(),
});

export type UntaggedRenewalTransactionsResponse = z.infer<
  typeof UntaggedRenewalTransactionsResponseSchema
>;

export const SalesSegmentTransactionsResponseSchema = z.object({
  transactions: z.array(salesTransactionRowSchema),
  total: z.number(),
});

export type SalesSegmentTransactionsResponse = z.infer<
  typeof SalesSegmentTransactionsResponseSchema
>;
